import { useEffect, useState, useCallback } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import api from "@/lib/api";

const Stat = ({ label, value, sub, testid }) => (
  <div data-testid={testid} className="border border-slate-200 bg-white p-8">
    <div className="font-mono text-5xl font-medium tracking-tighter text-ink md:text-6xl">
      {value}
    </div>
    <div className="mt-3 text-sm uppercase tracking-[0.15em] text-slate-500">{label}</div>
    {sub && <div className="mt-1 text-xs text-slate-400">{sub}</div>}
  </div>
);

const Bar = ({ name, value, max, testid }) => (
  <div data-testid={testid} className="py-2">
    <div className="mb-1.5 flex items-baseline justify-between">
      <span className="text-sm text-ink">{name}</span>
      <span className="font-mono text-sm text-slate-500">{value}</span>
    </div>
    <div className="h-2 w-full bg-slate-100">
      <div
        className="h-2 bg-cardinal"
        style={{ width: `${max ? Math.max((value / max) * 100, 4) : 0}%` }}
      />
    </div>
  </div>
);

export default function StatisticsPage() {
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    api
      .get("/stats")
      .then((res) => setData(res.data))
      .catch(() =>
        setData({ total: 0, roles: [], sentiment: {}, top_topics: [] })
      );
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 20000);
    return () => clearInterval(id);
  }, [load]);

  const s = data?.sentiment || {};
  const maxRole = Math.max(1, ...(data?.roles || []).map((r) => r.value));
  const maxTopic = Math.max(1, ...(data?.top_topics || []).map((t) => t.value));

  return (
    <div className="min-h-screen bg-white font-body text-ink">
      <Header />

      <section className="mx-auto max-w-7xl px-6 pb-12 pt-20 md:px-12 md:pt-28">
        <div className="text-xs uppercase tracking-[0.2em] text-cardinal">
          Live research data
        </div>
        <h1 className="mt-4 font-serif text-5xl font-medium tracking-tight text-ink md:text-6xl">
          What Californians are telling us
        </h1>
        <p className="mt-6 max-w-2xl text-lg leading-relaxed text-slate-700">
          These figures are aggregated from every experience submitted to the project. They
          update in real time. No individual story is ever shown here — only anonymous,
          combined data.
        </p>
        <div className="mt-4 flex items-center gap-2 text-xs text-slate-400">
          <span className="h-2 w-2 animate-pulse rounded-full bg-calblue" />
          Updates automatically
        </div>
      </section>

      {data === null ? (
        <div className="px-6 py-20 text-center text-slate-400 md:px-12">Loading…</div>
      ) : data.total === 0 ? (
        <div
          data-testid="stats-empty"
          className="mx-auto max-w-7xl px-6 pb-32 md:px-12"
        >
          <div className="border border-slate-200 bg-[#FAFAFA] p-16 text-center">
            <div className="font-mono text-6xl font-medium tracking-tighter text-ink">0</div>
            <h3 className="mt-4 font-serif text-2xl font-medium text-ink">
              Data collection has begun
            </h3>
            <p className="mx-auto mt-2 max-w-md text-slate-600">
              As Californians share their experiences, anonymous statistics will appear here
              for researchers, journalists, and policymakers to reference.
            </p>
          </div>
        </div>
      ) : (
        <section className="mx-auto max-w-7xl px-6 pb-32 md:px-12">
          {/* headline numbers */}
          <div className="grid grid-cols-1 gap-px bg-slate-200 sm:grid-cols-3">
            <Stat testid="stat-total" label="Voices Heard" value={data.total} />
            <Stat
              testid="stat-rated"
              label="Experiences Rated"
              value={s.total_rated || 0}
              sub="Positive / Mixed / Negative"
            />
            <Stat
              testid="stat-topics"
              label="Topics Identified"
              value={(data.top_topics || []).length}
              sub="By AI summarization"
            />
          </div>

          {/* sentiment */}
          <div className="mt-16">
            <h2 className="font-serif text-3xl font-medium tracking-tight text-ink">
              Overall sentiment
            </h2>
            <div className="mt-6 grid grid-cols-1 gap-px bg-slate-200 sm:grid-cols-3">
              <div data-testid="sentiment-positive" className="bg-white p-8">
                <div className="font-mono text-5xl font-medium tracking-tighter text-ink">
                  {s.positive_pct ?? 0}%
                </div>
                <div className="mt-2 text-sm uppercase tracking-[0.15em] text-slate-500">
                  Positive
                </div>
              </div>
              <div data-testid="sentiment-mixed" className="bg-white p-8">
                <div className="font-mono text-5xl font-medium tracking-tighter text-ink">
                  {s.mixed_pct ?? 0}%
                </div>
                <div className="mt-2 text-sm uppercase tracking-[0.15em] text-slate-500">
                  Mixed
                </div>
              </div>
              <div data-testid="sentiment-negative" className="bg-white p-8">
                <div className="font-mono text-5xl font-medium tracking-tighter text-ink">
                  {s.negative_pct ?? 0}%
                </div>
                <div className="mt-2 text-sm uppercase tracking-[0.15em] text-slate-500">
                  Negative
                </div>
              </div>
            </div>
          </div>

          {/* roles + topics */}
          <div className="mt-16 grid gap-16 lg:grid-cols-2">
            <div>
              <h2 className="font-serif text-3xl font-medium tracking-tight text-ink">
                Who is sharing
              </h2>
              <div className="mt-6 border border-slate-200 bg-white p-8">
                {(data.roles || []).map((r) => (
                  <Bar
                    key={r.name}
                    name={r.name}
                    value={r.value}
                    max={maxRole}
                    testid={`role-${r.name.toLowerCase().replace(/\s+/g, "-")}`}
                  />
                ))}
              </div>
            </div>
            <div>
              <h2 className="font-serif text-3xl font-medium tracking-tight text-ink">
                Topics mentioned most
              </h2>
              <div className="mt-6 border border-slate-200 bg-white p-8">
                {(data.top_topics || []).length === 0 ? (
                  <p className="text-sm text-slate-400">
                    Topics appear as experiences are analyzed.
                  </p>
                ) : (
                  (data.top_topics || []).map((t) => (
                    <Bar
                      key={t.name}
                      name={t.name}
                      value={t.value}
                      max={maxTopic}
                      testid={`topic-${t.name.toLowerCase().replace(/\s+/g, "-")}`}
                    />
                  ))
                )}
              </div>
            </div>
          </div>

          <p className="mt-16 max-w-2xl text-sm leading-relaxed text-slate-400">
            Methodology: Each submitted experience is read and summarized into structured,
            anonymized research categories. Sentiment reflects either the contributor's own
            description or a neutral assessment of the experience. Figures are provisional and
            update as new experiences are received.
          </p>
        </section>
      )}

      <Footer />
    </div>
  );
}
